/**
 * Parser for the data/stoerungen.xml file.
 */

import { format } from "date-fns"

import * as consts from "./constants"


/**
 * Detects parseerror nodes in the result of DOMParser.parseFromString
 * and raises exceptions in case of problems.
 */
export function detectParseErrors(xmlTree) {
    let parsererror = xmlTree.querySelector("parsererror")
    if (parsererror) {
        // Blink/Webkit
        const errorDiv = parsererror.querySelector("div")
        if (errorDiv) {
            throw new Error(errorDiv.innerHTML)
        }

        // Firefox
        if (parsererror.firstChild && parsererror.firstChild.textContent) {
            throw new Error(parsererror.firstChild.textContent)
        }

        throw new Error("Unbekannter Fehler beim Laden der XML-Quelldatei")
    }
}

/**
 * Parse a XML string into a datastructures.
 * Will throw Exceptions on XML or schema errors.
 *
 + @param rawXML {String}
 */
export default function parseDataXML(rawXML) {
    const xmlTree = new window.DOMParser().parseFromString(rawXML, "text/xml")
    detectParseErrors(xmlTree)

    const xmlRoot = Array.from(xmlTree.children)[0]
    const variant = xmlRoot.getAttribute("variant")
    const updated = xmlRoot.getAttribute("updated")

    if (xmlRoot.tagName !== "incidents") {
        throw new Error(
            `Root-Element des XML ist <${xmlRoot.tagName}>, und nicht <incidents>. Falsche Datei geladen?`,
        )
    }

    if (variant !== "Full" && variant !== "Private" && variant !== "Public") {
        throw new Error(`Ungültige Variante im XML definiert: '${variant}'`)
    }
    if (!updated) {
        throw new Error(`Attribut "updated" fehlt im XML-Root`)
    }

    //
    // Step 1: Find root elements
    //

    let outagesXML
    let powerqualitydropsXML

    for (let i = 0; i < xmlRoot.children.length; i++) {
        let tagName = xmlRoot.children[i].tagName

        if (tagName === "outages") {
            outagesXML = xmlRoot.children[i]
        }
        if (tagName === "powerqualitydrops") {
            powerqualitydropsXML = xmlRoot.children[i]
        }
    }

    if (!outagesXML) {
        outagesXML = { children: [] }
    }
    if (!powerqualitydropsXML) {
        powerqualitydropsXML = { children: [] }
    }

    //
    // Step 2: Find sub elements for each
    //

    let outages = {}
    let powerqualitydrops = {}

    for (let i = 0; i < outagesXML.children.length; i++) {
        let tagName = outagesXML.children[i].tagName
        outages[tagName] = parseFromXmlArray(
            Array.from(outagesXML.children[i].children),
            tagName,
        )
    }
    for (let i = 0; i < powerqualitydropsXML.children.length; i++) {
        let tagName = powerqualitydropsXML.children[i].tagName
        powerqualitydrops[tagName] = parseFromXmlArray(
            Array.from(powerqualitydropsXML.children[i].children),
            tagName,
        )
    }

    // Fill array for data that wasn't provided in the view data (all are optional)
    if (!Array.isArray(outages.stations)) {
        outages.stations = []
    }
    if (!Array.isArray(outages["construction-sites"])) {
        outages["construction-sites"] = []
    }
    if (!Array.isArray(outages.grosskunden)) {
        outages.grosskunden = []
    }
    if (!Array.isArray(outages.kritinfras)) {
        outages.kritinfras = []
    }

    return {
        variant: variant,
        updated: new Date(updated),
        outages: outages,
        powerqualitydrops: powerqualitydrops,
    }
}

function variantScore(variant) {
    switch (variant) {
        case "Public":
            return 0
        case "Private":
            return 1
        case "Full":
            return 2
        default:
            return -1
    }
}

/**
 * Merge multiple views from parseDataXML.
 *
 * Special handling:
 * - Takes the highest updated value
 * - Takes the highest variant value
 */
export function mergeParserResults(results) {
    let variant = "Public"
    let updated = +new Date(0)
    let outages = {}
    let powerqualitydrops = {}

    results.forEach(result => {
        Object.keys(result.outages).forEach(type => {
            if (!outages[type]) {
                outages[type] = []
            }
            outages[type] = [...outages[type], ...result.outages[type]]
        })

        Object.keys(result.powerqualitydrops).forEach(type => {
            if (!powerqualitydrops[type]) {
                powerqualitydrops[type] = []
            }
            powerqualitydrops[type] = [
                ...powerqualitydrops[type],
                ...result.powerqualitydrops[type],
            ]
        })

        if (+result.updated > +updated) {
            updated = result.updated
        }

        if (variantScore(result.variant) > variantScore(variant)) {
            variant = result.variant
        }
    })

    return {
        variant: variant,
        updated: updated,
        outages: outages,
        powerqualitydrops: powerqualitydrops,
    }
}

function parseFromXmlArray(xmlArray, type) {
    return xmlArray.map(node => {
        let netId = consts.NET_DEFAULT;
        if (node.attributes && node.attributes.sourceid) {
            netId = node.attributes.sourceid.value.toUpperCase();
        }

        let subType = "";
        if (node.attributes && node.attributes.outageType) {
            subType = node.attributes.outageType.value.toUpperCase();
        }

        console.log("type:", type);
        let dateFormatString = "dd.MM.yyyy HH:mm";
        if (type === "construction-sites") {
            dateFormatString = "dd.MM.yyyy";
        }

        let data = {
            // One of: NET_ID_KE / NET_ID_SF (uppercase)
            netId: netId,
            type: type,
            subType: subType
        }

        Array.from(node.children).forEach(subnode => {
            if (subnode.nodeName === "documents") {
                let docs = Array.from(subnode.children)
                let documents = []

                docs.forEach(docNode => {
                    documents.push({
                        title: docNode.getAttribute("title"),
                        url: docNode.getAttribute("url"),
                    })
                })
                data.documents = documents
                return
            }

            let nodeName = subnode.nodeName
            let value = subnode.textContent

            if (value.match(/^-?([0-9]+)$/)) {
                value = parseInt(value, 10)
            } else if (value.match(/^-?([0-9]+)\.([0-9]+)$/)) {
                value = parseFloat(value)
            }

            data[nodeName] = value

            if (nodeName === "timestamp") {
                data.rawTimestamp = data.timestamp
                if (data.timestamp && data.timestamp !== "") {
                    data.timestamp = parseXMLDate(data.timestamp)
                    data.timestampHuman = format(
                        data.timestamp,
                        dateFormatString
                    )

                } else {
                    data.timestamp = "";
                    data.timestampHuman = "";
                }
            }

            if (nodeName === "timestampEnd") {
                data.rawTimestampEnd = data.timestampEnd
                data.timestampEnd = parseXMLDate(data.timestampEnd)
                data.timestampEndHuman = format(
                    data.timestampEnd,
                    dateFormatString,
                )
            }
        })

        if (data.timestamp && data.timestampEnd) {
            // Ausfalldauer berechnen...
            data.duration = millisecondsToString(
                data.timestampEnd.getTime() - data.timestamp.getTime(),
            )
        }

        return data
    })
}

/**
 * Parse a timestamp from the XML field.
 *
 * @param timestamp {String}
 */
function parseXMLDate(timestamp) {
    let result = new Date(timestamp);
    if (result === null || isNaN(result.getTime())) {
        throw new Error(`Ungültiger Zeitstempel im XML: ${timestamp}`)
    }

    return result
}

/**
 * Convert milliseconds into a relative date string.
 *
 * @param milliseconds {Number}
 */
function millisecondsToString(milliseconds) {
    let seconds = Math.abs(milliseconds / 1000)
    return (
        parseInt(seconds / 60 / 60 / 24, 10) +
        " Tage " +
        addLeadingZero((seconds / 60 / 60) % 24) +
        ":" +
        addLeadingZero((seconds / 60) % 60) +
        ":" +
        addLeadingZero(seconds % 60)
    )
}

/**
 * Add a leading zero to values below 10.
 *
 * @param value {Number}
 */
function addLeadingZero(value) {
    if (value < 10) {
        return "0" + parseInt(value, 10)
    } else {
        return parseInt(value, 10)
    }
}
